import { graphql } from '@/lib/graphql';

export const CallDetailLogSubscription = graphql(`
  subscription CallDetailLog($organizationId: Uuid!, $callControlId: String!) {
    callLogs(
      where: { organizationId: { _eq: $organizationId }, callControlId: { _eq: $callControlId } }
      orderBy: { startedAt: DESC }
      limit: 1
    ) {
      id
      fromNumber
      toNumber
      direction
      status
      durationSeconds
      collectedData
      startedAt
      endedAt
      flow {
        id
        name
      }
      phoneNumber {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`);

export const CallDetailSessionSubscription = graphql(`
  subscription CallDetailSession($organizationId: Uuid!, $callControlId: String!) {
    callSessions(
      where: { organizationId: { _eq: $organizationId }, callControlId: { _eq: $callControlId } }
      orderBy: { startedAt: DESC }
      limit: 1
    ) {
      id
      fromNumber
      toNumber
      direction
      status
      variables
      startedAt
      endedAt
      flow {
        id
        name
      }
      phoneNumber {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`);

export const CallDetailStepsSubscription = graphql(`
  subscription CallDetailSteps($organizationId: Uuid!, $callControlId: String!) {
    callSteps(
      where: { organizationId: { _eq: $organizationId }, callControlId: { _eq: $callControlId } }
      orderBy: { occurredAt: ASC }
    ) {
      id
      nodeName
      widgetType
      handleTaken
      input
      output
      occurredAt
    }
  }
`);

export const CallDetailRecordingsSubscription = graphql(`
  subscription CallDetailRecordings($organizationId: Uuid!, $callControlId: String!) {
    callRecordings(
      where: { organizationId: { _eq: $organizationId }, callControlId: { _eq: $callControlId } }
      orderBy: { startedAt: ASC }
    ) {
      id
      recordingId
      format
      recordingUrls
      publicRecordingUrls
      startedAt
      endedAt
    }
  }
`);

export const CallDetailVoicemailsSubscription = graphql(`
  subscription CallDetailVoicemails($organizationId: Uuid!, $callControlId: String!) {
    voicemails(
      where: { organizationId: { _eq: $organizationId }, callControlId: { _eq: $callControlId } }
      orderBy: { createdAt: ASC }
    ) {
      id
      status
      durationSeconds
      createdAt
      s3Key
      recordingUrl
      publicRecordingUrl
    }
  }
`);

export const CallDetailErrorsSubscription = graphql(`
  subscription CallDetailErrors($organizationId: Uuid!, $callControlId: String!) {
    callErrors(
      where: { organizationId: { _eq: $organizationId }, callControlId: { _eq: $callControlId } }
      orderBy: { occurredAt: ASC }
    ) {
      id
      severity
      errorType
      message
      nodeName
      details
      occurredAt
      reviewedAt
    }
  }
`);

export const CallErrorSummarySubscription = graphql(`
  subscription CallErrorSummary($organizationId: Uuid!, $callControlIds: [String!]!) {
    callErrors(where: { organizationId: { _eq: $organizationId }, callControlId: { _in: $callControlIds } }) {
      id
      callControlId
      severity
    }
  }
`);

export const RecentCallsSubscription = graphql(`
  subscription RecentCalls($organizationId: Uuid!, $limit: Int!) {
    callLogs(where: { organizationId: { _eq: $organizationId } }, orderBy: { startedAt: DESC }, limit: $limit) {
      id
      callControlId
      fromNumber
      toNumber
      direction
      status
      durationSeconds
      startedAt
      flow {
        id
        name
      }
      phoneNumber {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`);

export const CallStatsTotalsSubscription = graphql(`
  subscription CallStatsTotals($organizationId: Uuid!, $startedAt: Timestamptz!) {
    callLogsAggregate(where: { organizationId: { _eq: $organizationId }, startedAt: { _gte: $startedAt } }) {
      aggregate {
        count
        sum {
          durationSeconds
        }
        avg {
          durationSeconds
        }
      }
    }
  }
`);

export const CallStatsByStatusSubscription = graphql(`
  subscription CallStatsByStatus($organizationId: Uuid!, $startedAt: Timestamptz!, $status: String!) {
    callLogsAggregate(
      where: { organizationId: { _eq: $organizationId }, startedAt: { _gte: $startedAt }, status: { _eq: $status } }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const ActiveCallSessionsSubscription = graphql(`
  subscription ActiveCallSessions($organizationId: Uuid!, $limit: Int!, $offset: Int!) {
    callSessions(
      where: { organizationId: { _eq: $organizationId }, status: { _eq: "active" } }
      orderBy: { startedAt: DESC }
      limit: $limit
      offset: $offset
    ) {
      id
      callControlId
      fromNumber
      toNumber
      direction
      currentNodeId
      startedAt
    }
  }
`);

export const ActiveCallSessionsCountSubscription = graphql(`
  subscription ActiveCallSessionsCount($organizationId: Uuid!) {
    callSessionsAggregate(where: { organizationId: { _eq: $organizationId }, status: { _eq: "active" } }) {
      aggregate {
        count
      }
    }
  }
`);

export const OrganizationFlowsSubscription = graphql(`
  subscription OrganizationFlows($organizationId: Uuid!) {
    flows(where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }, orderBy: { name: ASC }) {
      id
      name
      description
      currentPublicationId
      updatedAt
    }
  }
`);

export const LiveCallsSubscription = graphql(`
  subscription LiveCalls($where: CallSessionsBoolExp!) {
    callSessions(where: $where, orderBy: { startedAt: DESC }) {
      id
      callControlId
      fromNumber
      toNumber
      direction
      startedAt
      flow {
        id
        name
      }
      phoneNumber {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`);

export const CallErrorsSubscription = graphql(`
  subscription CallErrors($where: CallErrorsBoolExp!, $limit: Int!) {
    callErrors(where: $where, orderBy: { occurredAt: DESC }, limit: $limit) {
      id
      callControlId
      fromNumber
      toNumber
      errorType
      severity
      message
      nodeName
      widgetType
      details
      occurredAt
      reviewedAt
      flow {
        id
        name
      }
      phoneNumber {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`);

export const MarkCallErrorReviewedMutation = graphql(`
  mutation MarkCallErrorReviewed($id: Uuid!, $reviewedAt: Timestamptz!) {
    updateCallErrorsByPk(pkColumns: { id: $id }, _set: { reviewedAt: $reviewedAt }) {
      id
      reviewedAt
    }
  }
`);

export const MarkAllCallErrorsReviewedMutation = graphql(`
  mutation MarkAllCallErrorsReviewed($organizationId: Uuid!, $reviewedAt: Timestamptz!) {
    updateCallErrors(
      where: { organizationId: { _eq: $organizationId }, reviewedAt: { _isNull: true } }
      _set: { reviewedAt: $reviewedAt }
    ) {
      affectedRows
    }
  }
`);

export const SearchCallLogsSubscription = graphql(`
  subscription SearchCallLogs($where: CallLogsBoolExp!, $limit: Int!, $offset: Int!) {
    callLogs(where: $where, orderBy: { startedAt: DESC }, limit: $limit, offset: $offset) {
      id
      callControlId
      fromNumber
      toNumber
      direction
      status
      durationSeconds
      startedAt
      flow {
        id
        name
      }
      phoneNumber {
        id
        friendlyName
        phoneNumber
      }
    }
  }
`);

export const SearchCallLogsCountSubscription = graphql(`
  subscription SearchCallLogsCount($where: CallLogsBoolExp!) {
    callLogsAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const FlowCallStatsSubscription = graphql(`
  subscription FlowCallStats($organizationId: Uuid!, $startedAt: Timestamptz!) {
    flows(where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }, orderBy: { name: ASC }) {
      id
      name
      total: callLogsAggregate(where: { startedAt: { _gte: $startedAt } }) {
        aggregate {
          count
          avg {
            durationSeconds
          }
        }
      }
      completed: callLogsAggregate(
        where: { startedAt: { _gte: $startedAt }, status: { _in: ["completed", "reaped"] } }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

export const CallVolumeSeriesSubscription = graphql(`
  subscription CallVolumeSeries($organizationId: Uuid!, $startedAt: Timestamptz!) {
    callLogs(
      where: { organizationId: { _eq: $organizationId }, startedAt: { _gte: $startedAt } }
      orderBy: { startedAt: ASC }
    ) {
      id
      startedAt
      status
    }
  }
`);

export const OrganizationPhoneNumbersSubscription = graphql(`
  subscription OrganizationPhoneNumbers($organizationId: Uuid!) {
    phoneNumbers(where: { organizationId: { _eq: $organizationId } }, orderBy: { phoneNumber: ASC }) {
      id
      phoneNumber
      friendlyName
      flowId
      status
    }
  }
`);
